import { useState, useCallback } from "react";
import type { EvaluationCriterion, CreateCriterionPayload, UpdateCriterionPayload } from "../../../../types/criteria.type";
import { EMPTY_FORM, type CriterionFormValues } from "./criteria-form.types";

interface UseCriterionFormOptions {
	addCriterion: (payload: CreateCriterionPayload) => Promise<EvaluationCriterion | null>;
	editCriterion: (criterionId: number, payload: UpdateCriterionPayload) => Promise<EvaluationCriterion | null>;
}

export interface UseCriterionFormResult {
	editingId: number | null;
	formError: string | null;
	formValues: CriterionFormValues;
	isAdding: boolean;
	setFormValues: React.Dispatch<React.SetStateAction<CriterionFormValues>>;
	handleCancel: () => void;
	handleSaveEdit: () => Promise<void>;
	handleSaveNew: () => Promise<void>;
	handleStartAdd: () => Promise<void>;
	handleStartEdit: (criterion: EvaluationCriterion) => Promise<void>;
	updateField: (field: keyof CriterionFormValues, value: string) => void;
}

export const useCriterionForm = ({
	addCriterion,
	editCriterion,
}: UseCriterionFormOptions): UseCriterionFormResult => {
	const [isAdding, setIsAdding] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [formValues, setFormValues] = useState<CriterionFormValues>(EMPTY_FORM);
	const [formError, setFormError] = useState<string | null>(null);

	const validateForm = useCallback((): boolean => {
		if (!formValues.name.trim()) {
			setFormError("Name is required");
			return false;
		}
		const weight = Number(formValues.weight);
		if (isNaN(weight) || weight < 0 || weight > 100) {
			setFormError("Weight must be between 0 and 100");
			return false;
		}
		setFormError(null);
		return true;
	}, [formValues.name, formValues.weight]);

	/**
	 * Persist whatever row is currently being edited/added before switching to
	 * another row (PRO criteria UX: editing one row then clicking another should
	 * save, not discard — only an explicit Cancel discards). Returns false to
	 * abort the switch when the in-progress row is invalid (so data isn't lost).
	 */
	const commitPending = useCallback(async (): Promise<boolean> => {
		if (editingId !== null) {
			if (!validateForm()) {
				return false;
			}
			const payload: UpdateCriterionPayload = {
				id: editingId,
				name: formValues.name.trim(),
				description: formValues.description.trim() || undefined,
				rfx_evaluation_type_id: formValues.rfx_evaluation_type_id,
				weight: Number(formValues.weight),
			};
			const result = await editCriterion(editingId, payload);
			return !!result;
		}

		if (isAdding) {
			const hasContent =
				formValues.name.trim() !== "" ||
				formValues.description.trim() !== "" ||
				formValues.weight.trim() !== "";
			if (!hasContent) {
				return true;
			}
			if (!validateForm()) {
				return false;
			}
			const payload: CreateCriterionPayload = {
				name: formValues.name.trim(),
				description: formValues.description.trim() || undefined,
				rfx_evaluation_type_id: formValues.rfx_evaluation_type_id,
				weight: Number(formValues.weight),
			};
			const result = await addCriterion(payload);
			return !!result;
		}

		return true;
	}, [editingId, isAdding, validateForm, formValues, editCriterion, addCriterion]);

	const handleStartAdd = useCallback(async (): Promise<void> => {
		const ok = await commitPending();
		if (!ok) {
			return;
		}
		setIsAdding(true);
		setEditingId(null);
		setFormValues(EMPTY_FORM);
		setFormError(null);
	}, [commitPending]);

	const handleStartEdit = useCallback(async (criterion: EvaluationCriterion): Promise<void> => {
		if (editingId === criterion.id) {
			return;
		}
		const ok = await commitPending();
		if (!ok) {
			return;
		}
		setEditingId(criterion.id);
		setIsAdding(false);
		setFormValues({
			name: criterion.name,
			description: criterion.description ?? "",
			rfx_evaluation_type_id: criterion.rfx_evaluation_type_id,
			weight: String(criterion.weight),
		});
		setFormError(null);
	}, [editingId, commitPending]);

	const handleCancel = useCallback((): void => {
		setIsAdding(false);
		setEditingId(null);
		setFormValues(EMPTY_FORM);
		setFormError(null);
	}, []);

	const updateField = useCallback(
		(field: keyof CriterionFormValues, value: string): void => {
			setFormValues((prev) => ({ ...prev, [field]: value }));
			setFormError(null);
		},
		[],
	);

	const handleSaveNew = useCallback(async (): Promise<void> => {
		if (!validateForm()) return;

		const payload: CreateCriterionPayload = {
			name: formValues.name.trim(),
			description: formValues.description.trim() || undefined,
			rfx_evaluation_type_id: formValues.rfx_evaluation_type_id,
			weight: Number(formValues.weight),
		};
		const result = await addCriterion(payload);
		if (result) {
			setIsAdding(false);
			setFormValues(EMPTY_FORM);
		}
	}, [validateForm, formValues, addCriterion]);

	const handleSaveEdit = useCallback(async (): Promise<void> => {
		if (editingId === null || !validateForm()) return;

		const payload: UpdateCriterionPayload = {
			id: editingId,
			name: formValues.name.trim(),
			description: formValues.description.trim() || undefined,
			rfx_evaluation_type_id: formValues.rfx_evaluation_type_id,
			weight: Number(formValues.weight),
		};
		const result = await editCriterion(editingId, payload);
		if (result) {
			setEditingId(null);
			setFormValues(EMPTY_FORM);
		}
	}, [editingId, validateForm, formValues, editCriterion]);

	return {
		isAdding,
		editingId,
		formValues,
		formError,
		setFormValues,
		updateField,
		handleStartAdd,
		handleStartEdit,
		handleCancel,
		handleSaveNew,
		handleSaveEdit,
	};
};
